/**
 * FaceNumberRing Component
 *
 * Displays a ring of dice face numbers around the camera edge during registration.
 * Shows which faces are visible from the sides when a specific face is on top.
 * The ring rotates clockwise with each capture to guide dice positioning.
 *
 * Ring positions vary by dice type:
 * - D4: 3 positions at 120 degrees (triangle)
 * - D6: 4 positions at 90 degrees (square)
 * - D8: 3 positions at 120 degrees (triangle)
 * - D10/D12/D20: 5 positions at 72 degrees (pentagon)
 */

import { DiceType, DICE_GEOMETRY } from '@dice-capture/shared/types/dice-registry';
import { useMemo, useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

/** Container dimensions for aspect-ratio-aware positioning */
interface ContainerDimensions {
  width: number;
  height: number;
}

const SERVICE_TAG = '[FACE_NUMBER_RING]';

/**
 * Props for the FaceNumberRing component
 */
export interface FaceNumberRingProps {
  /** Dice type being registered */
  diceType: DiceType;
  /** Current face number on top (being captured). For D10, use 0-9. */
  currentFace: number;
  /** Current rotation position (0-based, determines ring rotation offset) */
  currentPosition: number;
  /** Total rotation positions - ring completes one full cycle over this many steps */
  totalPositions: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Represents a positioned face number in the ring
 */
interface RingPosition {
  /** Face number (internal, 1-based) */
  face: number;
  /** Display value (for D10: face 10 displays as "0") */
  displayValue: string;
  /** X position as percentage (0-100) */
  x: number;
  /** Y position as percentage (0-100) */
  y: number;
}

/**
 * Converts internal face number to display value.
 * D10 special case: face 10 displays as "0" (matching physical dice).
 */
function getDisplayValue(face: number, diceType: DiceType): string {
  if (diceType === 'd10' && face === 10) {
    return '0';
  }
  return String(face);
}

/**
 * Calculates the position of each number around the ring.
 * Numbers are evenly distributed and rotate based on current capture position.
 * TICKET-119: Ring is constrained to the smaller container dimension to form a true circle.
 *
 * @param visibleFaces - Array of face numbers visible from the side
 * @param ringAngle - Angle between each ring position in degrees
 * @param currentPosition - Current rotation index (0-based)
 * @param totalPositions - Total rotation steps (ring completes one cycle over this many)
 * @param diceType - Dice type for display value conversion (D10 shows "0" for face 10)
 * @param dimensions - Container dimensions for aspect-ratio-aware positioning
 * @returns Array of positioned face numbers
 */
function calculateRingPositions(
  visibleFaces: number[],
  ringAngle: number,
  currentPosition: number,
  totalPositions: number,
  diceType: DiceType,
  dimensions: ContainerDimensions
): RingPosition[] {
  const count = visibleFaces.length;
  const { width, height } = dimensions;

  // Return empty if no faces or dimensions not yet measured
  if (count === 0 || width === 0 || height === 0) return [];

  // Calculate rotation offset based on current position
  // Ring completes one full cycle (ringAngle degrees) over totalPositions steps
  const rotationPerStep = totalPositions > 0 ? ringAngle / totalPositions : 0;
  const rotationOffset = currentPosition * rotationPerStep;

  // TICKET-119: Ring constrained to smaller dimension to always form a true circle
  // Use 38% of the smaller dimension as the radius
  const constrainedSize = Math.min(width, height);
  const radiusPx = constrainedSize * 0.38;

  return visibleFaces.map((face, index) => {
    // Start at 12 o'clock (-90 degrees) and distribute clockwise
    const baseAngle = index * ringAngle - 90;
    const angle = baseAngle + rotationOffset;

    // Convert polar to cartesian coordinates (in pixels from center)
    const radians = angle * (Math.PI / 180);
    const pixelX = radiusPx * Math.cos(radians);
    const pixelY = radiusPx * Math.sin(radians);

    // Convert pixel offsets to percentages relative to container dimensions
    // This ensures a true circle regardless of aspect ratio
    const x = 50 + (pixelX / width) * 100;
    const y = 50 + (pixelY / height) * 100;

    return { face, displayValue: getDisplayValue(face, diceType), x, y };
  });
}

/**
 * FaceNumberRing displays visible side faces around the camera edge.
 * Guides users on dice positioning during registration capture.
 */
export function FaceNumberRing({
  diceType,
  currentFace,
  currentPosition,
  totalPositions,
  className
}: FaceNumberRingProps) {
  const geometry = DICE_GEOMETRY[diceType];
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<ContainerDimensions>({ width: 0, height: 0 });

  // TICKET-119: Measure container dimensions to calculate aspect-ratio-aware ring
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Get visible side faces for the current top face
  const visibleFaces = useMemo(() => {
    const faces = geometry.sideMap[currentFace];
    if (!faces) {
      console.warn(`${SERVICE_TAG} No side map for face`, {
        diceType,
        currentFace,
        fallbackValue: []
      });
      return [];
    }
    return faces;
  }, [geometry, currentFace, diceType]);

  // Calculate ring positions with rotation offset and aspect-ratio awareness
  const ringPositions = useMemo(() => {
    return calculateRingPositions(
      visibleFaces,
      geometry.ringAngle,
      currentPosition,
      totalPositions,
      diceType,
      dimensions
    );
  }, [visibleFaces, geometry.ringAngle, currentPosition, totalPositions, diceType, dimensions]);

  // Don't render until we have dimensions (prevents flash of incorrect positions)
  if (ringPositions.length === 0 || dimensions.width === 0) {
    return (
      <div
        ref={containerRef}
        data-testid="face-number-ring"
        className={cn('absolute inset-0 pointer-events-none', className)}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      data-testid="face-number-ring"
      className={cn('absolute inset-0 pointer-events-none', className)}
      role="img"
      aria-label={`Side faces visible: ${visibleFaces.join(', ')}`}
    >
      {ringPositions.map(({ face: _face, displayValue, x, y }, index) => (
        <div
          key={`ring-${currentFace}-${index}`}
          data-testid={`ring-number-${displayValue}`}
          className={cn(
            'absolute w-8 h-8 flex items-center justify-center',
            'transition-all duration-300 ease-out',
            'text-white font-bold text-lg',
            'bg-black/50 rounded-full backdrop-blur-sm',
            'border border-white/40'
          )}
          style={{
            left: `${x}%`,
            top: `${y}%`,
            transform: 'translate(-50%, -50%)'
          }}
          aria-hidden="true"
        >
          {displayValue}
        </div>
      ))}
    </div>
  );
}

export default FaceNumberRing;
